<script>
  import { onMount } from 'svelte';
  
  export let checkInterval = 60 * 60 * 1000;
  export let buttonText = 'Update Now';
  export let notificationText = 'A new version is available!';
  export let showNotification = true;
  export let customClass = '';
  
  let updateAvailable = false;
  let registration;
  let checkIntervalId;
  let isUpdating = false;
  let isDismissed = false;
  let lastUpdateTime = 0;

  // Check local storage for last update time
  function getLastUpdateTime() {
    try {
      const storedTime = localStorage.getItem('pwa_last_update_time');
      return storedTime ? parseInt(storedTime, 10) : 0;
    } catch (e) {
      return 0;
    }
  }

  // Store last update time
  function setLastUpdateTime(time) {
    try {
      localStorage.setItem('pwa_last_update_time', time.toString());
    } catch (e) {
      console.error('Could not save to localStorage:', e);
    }
  }

  onMount(async () => {
    // Retrieve last update time
    lastUpdateTime = getLastUpdateTime();
    const currentTime = Date.now();
    
    // Only check for updates if it's been more than 1 hour since last update
    const shouldCheckForUpdates = (currentTime - lastUpdateTime) > (60 * 60 * 1000);
    
    if ('serviceWorker' in navigator) {
      try {
        registration = await navigator.serviceWorker.register('/service-worker.js');
        
        // Check for existing waiting service worker only if we should check
        if (shouldCheckForUpdates && registration.waiting) {
          updateAvailable = true;
        }
        
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Only show update if we haven't updated recently
              if (shouldCheckForUpdates) {
                updateAvailable = true;
              }
            }
          });
        });

        if (checkInterval > 0 && shouldCheckForUpdates) {
          checkIntervalId = setInterval(checkForUpdates, checkInterval);
          // Initial check only if we should
          if (shouldCheckForUpdates) {
            checkForUpdates();
          }
        }
        
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }

    return () => {
      if (checkIntervalId) {
        clearInterval(checkIntervalId);
      }
    };
  });

  async function forceUpdate() {
    if (!registration || !registration.waiting || isUpdating) return;
    
    isUpdating = true;
    updateAvailable = false;

    try {
      // Record update time
      setLastUpdateTime(Date.now());
      
      const reloadPromise = new Promise((resolve) => {
        setTimeout(resolve, 1000); // Ensure minimum time for service worker to take control
      });

      registration.waiting.postMessage('skipWaiting');
      await reloadPromise;
      window.location.reload();
    } catch (error) {
      console.error('Update failed:', error);
      isUpdating = false;
      updateAvailable = true;
    }
  }

  function dismissNotification() {
    isDismissed = true;
    updateAvailable = false;
  }

  async function checkForUpdates() {
    if (registration && !isUpdating) {
      try {
        await registration.update();
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }
  }
</script>

{#if updateAvailable && showNotification && !isUpdating && !isDismissed}
  <div class="pwa-update-notification {customClass}" role="alert">
    <p>{notificationText}</p>
    <div class="pwa-update-actions">
      <button 
        on:click={forceUpdate}
        class="pwa-update-button"
        disabled={isUpdating}
      >
        {buttonText}
      </button>
      <button 
        on:click={dismissNotification}
        class="pwa-close-button"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  </div>
{/if}

<style>
  .pwa-update-notification {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    z-index: 1000;
    max-width: 320px;
  }

  .pwa-update-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pwa-update-button {
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .pwa-update-button:hover {
    background-color: #2563eb;
  }

  .pwa-update-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .pwa-close-button {
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 20px;
    line-height: 1;
    padding: 4px 8px;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pwa-close-button:hover {
    background-color: #f1f5f9;
    color: #334155;
  }

  p {
    margin: 0;
    color: #1f2937;
    flex: 1;
  }
</style>